<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col md="6">
          <b-form-group label="Seleccione una Carrera">
            <v-select
              v-model="selectedProgram"
              :options="programs"
              label="nombre"
              value="carreraId"
              @input="loadPensum"
              :loading="loading"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="text-right">
          <b-button 
            variant="success" 
            @click="savePensum"
            :disabled="!selectedProgram"
          >
            <feather-icon icon="SaveIcon" class="mr-50" />
            Guardar Pensum
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-row v-if="selectedProgram">
      <!-- Columna de Cursos Disponibles -->
      <b-col md="4">
        <b-card title="Cursos Disponibles">
          <draggable
            v-model="availableCourses"
            class="list-group list-group-flush cursor-move"
            v-bind="dragOptions"
            group="courses"
          >
            <b-list-group-item
              v-for="course in availableCourses"
              :key="course.cursoId"
              class="d-flex justify-content-between align-items-center"
            >
              <span>{{ course.nombre }}</span>
              <b-badge>{{ course.creditos }} créd.</b-badge>
            </b-list-group-item>
          </draggable>
        </b-card>
      </b-col>

      <!-- Columna de Pénsum por Semestres -->
      <b-col md="8">
        <b-card>
          <b-tabs>
            <b-tab
              v-for="(semester, index) in pensum.semesters"
              :key="index"
              :title="`Semestre ${index + 1}`"
            >
              <draggable
                v-model="semester.courses"
                class="list-group list-group-flush cursor-move min-h-200"
                v-bind="dragOptions"
                group="courses"
              >
                <b-list-group-item
                  v-for="course in semester.courses"
                  :key="course.cursoId"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <feather-icon icon="BookOpenIcon" class="mr-50" />
                    <span>{{ course.nombre }}</span>
                  </div>
                  <div>
                    <b-badge variant="info" class="mr-1">
                      Req: {{ getPrerequisites(course) }}
                    </b-badge>
                    <b-button variant="flat-danger" class="btn-icon" size="sm" @click="removeCourse(semester, course)">
                      <feather-icon icon="Trash2Icon" />
                    </b-button>
                  </div>
                </b-list-group-item>
              </draggable>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BTabs, BTab, BListGroupItem, BBadge, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import carrerasService from '@/services/carrerasService'
import cursosService from '@/services/cursosService'
import pensumsService from '@/services/pensumsService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BTabs,
    BTab,
    BListGroupItem,
    BBadge,
    BButton,
    vSelect,
    draggable,
  },
  data() {
    return {
      programs: [],
      allCourses: [],
      selectedProgram: null,
      pensum: {
        pensumId: null,
        carreraId: null,
        año: new Date().getFullYear(),
        semesters: [],
      },
      availableCourses: [],
      loading: false,
      dragOptions: {
        animation: 200,
        ghostClass: 'ghost-card',
      },
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadPrograms(),
          this.loadCourses()
        ])
      } catch (error) {
        console.error('Error al cargar datos:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar los datos',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async loadPrograms() {
      try {
        const response = await carrerasService.getAll()
        this.programs = response
      } catch (error) {
        console.error('Error al cargar carreras:', error)
        throw error
      }
    },
    async loadCourses() {
      try {
        const response = await cursosService.getAll()
        this.allCourses = response
      } catch (error) {
        console.error('Error al cargar cursos:', error)
        throw error
      }
    },
    async loadPensum() {
      if (!this.selectedProgram) {
        this.pensum = { 
          pensumId: null, 
          carreraId: null, 
          año: new Date().getFullYear(),
          semesters: [] 
        }
        this.availableCourses = []
        return
      }
      
      try {
        // Buscar pensums existentes para la carrera seleccionada
        const existingPensums = await pensumsService.getByCarrera(this.selectedProgram.carreraId)
        
        if (existingPensums && existingPensums.length > 0) {
          // Cargar el pensum más reciente
          const latestPensum = existingPensums[0]
          console.log('Pensum encontrado:', latestPensum)
          
          // Crear estructura de semestres
          this.pensum = {
            pensumId: latestPensum.pensumId,
            carreraId: latestPensum.carreraId,
            año: latestPensum.año,
            semesters: Array.from({ length: 10 }, (_, index) => ({ 
              numero: index + 1, 
              courses: [] 
            }))
          }
          
          // Cargar cursos existentes del pensum
          try {
            const cursosAsignados = await pensumsService.getCursos(latestPensum.pensumId)
            console.log('Cursos asignados encontrados:', cursosAsignados)
            
            // Organizar cursos en semestres
            this.organizarCursosEnSemestres(cursosAsignados)
          } catch (error) {
            console.error('Error al cargar cursos del pensum:', error)
          }
          
          console.log('Estructura de pensum creada:', this.pensum)
        } else {
          // Crear nuevo pensum vacío
          this.pensum = {
            pensumId: null,
            carreraId: this.selectedProgram.carreraId,
            año: new Date().getFullYear(),
            semesters: Array.from({ length: 10 }, (_, index) => ({ 
              numero: index + 1, 
              courses: [] 
            }))
          }
          console.log('Nuevo pensum creado:', this.pensum)
        }
        
        this.updateAvailableCourses()
      } catch (error) {
        console.error('Error al cargar pensum:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar el pensum',
          },
        })
      }
    },
    updateAvailableCourses() {
      const assignedCourseIds = this.pensum.semesters.flatMap(s => s.courses.map(c => c.cursoId))
      this.availableCourses = this.allCourses.filter(c => !assignedCourseIds.includes(c.cursoId))
    },
    organizarCursosEnSemestres(cursosAsignados) {
      // Limpiar todos los semestres primero
      this.pensum.semesters.forEach(semestre => {
        semestre.courses = []
      })
      
      // Organizar cursos por semestre
      cursosAsignados.forEach(asignacion => {
        // Ahora el backend debe devolver el semestre directamente
        const numeroSemestre = asignacion.semestre || this.extraerSemestreDeNombre(asignacion.nombre)
        const semestre = this.pensum.semesters.find(s => s.numero === numeroSemestre)
        
        if (semestre) {
          // El nombre del curso NO debe modificarse
          semestre.courses.push({
            cursoId: asignacion.cursoId,
            codigo: asignacion.codigo,
            nombre: asignacion.nombre, // Nombre original sin modificar
            creditos: asignacion.creditos,
            estado: asignacion.estado,
            cursoPrerrequisitoId: asignacion.cursoPrerrequisitoId
          })
          console.log(`Curso ${asignacion.codigo} (${asignacion.nombre}) asignado al semestre ${numeroSemestre}`)
        }
      })
      
      // Actualizar cursos disponibles
      this.updateAvailableCourses()
    },
    extraerSemestreDeNombre(nombre) {
      // Método de respaldo por si el backend aún devuelve nombres con [Sem X]
      const match = nombre.match(/\[Sem (\d+)\]/)
      if (match) {
        return parseInt(match[1])
      }
      return 1 // Por defecto semestre 1
    },
    getPrerequisites(course) {
      if (!course.cursoPrerrequisitoId) return 'Ninguno'
      const prereqCourse = this.allCourses.find(c => c.cursoId === course.cursoPrerrequisitoId)
      return prereqCourse ? `${prereqCourse.codigo} - ${prereqCourse.nombre}` : 'N/A'
    },
    removeCourse(semester, course) {
      const index = semester.courses.findIndex(c => c.cursoId === course.cursoId)
      if (index > -1) {
        semester.courses.splice(index, 1)
        this.updateAvailableCourses()
      }
    },
    async savePensum() {
      try {
        console.log('Guardando pensum:', this.pensum)
        
        if (this.pensum.pensumId) {
          // Actualizar pensum existente y su estructura
          const updateData = {
            pensumId: this.pensum.pensumId,
            carreraId: this.pensum.carreraId,
            año: this.pensum.año,
            estado: true
          }
          console.log('Datos de actualización:', updateData)
          await pensumsService.update(this.pensum.pensumId, updateData)
          
          // Guardar la estructura de semestres y cursos
          await this.guardarEstructura()
        } else {
          // Crear nuevo pensum
          const createData = {
            carreraId: this.pensum.carreraId,
            año: this.pensum.año,
            estado: true
          }
          console.log('Datos de creación:', createData)
          const response = await pensumsService.create(createData)
          this.pensum.pensumId = response.pensumId
          
          // Guardar la estructura de semestres y cursos
          if (this.pensum.semesters && this.pensum.semesters.length > 0) {
            await this.guardarEstructura()
          }
        }
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Éxito',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Pensum guardado correctamente',
          },
        })
        
        // Recargar el pensum para ver los datos actualizados
        await this.loadPensum()
      } catch (error) {
        console.error('Error al guardar pensum:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || 'Error al guardar el pensum',
          },
        })
      }
    },
    async guardarEstructura() {
      try {
        console.log('Guardando estructura de cursos...')
        // Usar el método que guarda toda la estructura de una vez
        await pensumsService.guardarEstructuraCompleta(
          this.pensum.pensumId, 
          this.pensum.semesters
        )
        console.log('Estructura guardada exitosamente')
      } catch (error) {
        console.error('Error al guardar estructura:', error)
        // Si el endpoint no está implementado aún, intentar con el método antiguo
        console.warn('Intentando guardar con método alternativo...')
        throw error
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.min-h-200 {
  min-height: 200px;
}
.ghost-card {
  opacity: 0.5;
  background: #F7FAFC;
  border: 1px solid #4299e1;
}
</style>
